import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { X, Sliders, LayoutTemplate } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDashboardById } from '@/hooks/useDashboards';
import { DashboardOverlayContent } from './DashboardOverlayContent';

interface ControlsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  controlsDashboardId?: string | null;
}

export const ControlsOverlay: React.FC<ControlsOverlayProps> = ({
  isOpen,
  onClose,
  controlsDashboardId,
}) => {
  const navigate = useNavigate();
  const { dashboard } = useDashboardById(controlsDashboardId);

  const handleCloseClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    },
    [onClose]
  );

  if (!isOpen) return null;

  const content = (
    <div
      className="fixed inset-0 z-[100] flex justify-center pointer-events-none"
    >
      {/* Panel - frosted glass / glassmorphism style */}
      <div
        className={cn(
          'pointer-events-auto w-full max-w-2xl mx-4 mt-0 h-[30vh] min-h-0 overflow-hidden rounded-b-xl',
          'backdrop-blur-xl',
          'animate-in slide-in-from-top duration-300'
        )}
        style={{
          background: 'rgba(255, 255, 255, 0.25)',
        }}
      >
        <div className="flex flex-col h-full">
          {/* Header - compact */}
          <div className="flex items-center justify-between px-4 py-2">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Sliders className="h-5 w-5" />
              Controls
            </h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCloseClick}
              className="shrink-0 z-10"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Body - widgets moved up, no scroll */}
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="flex-1 min-h-0 min-w-0 overflow-hidden px-4 pt-0 pb-2">
              {controlsDashboardId ? (
                <DashboardOverlayContent
                  dashboardId={controlsDashboardId}
                  emptyMessage="No widgets yet. Add components in the Dashboard Builder."
                  className="mb-1"
                  compact={dashboard?.preview_compact ?? false}
                  glass
                />
              ) : (
                <p className="text-sm text-muted-foreground mb-3">
                  No Controls dashboard for this brand. You see your personal one first, then the brand&apos;s shared, then the global default. Create or choose one in the Dashboard Builder.
                </p>
              )}
            </div>

            <Button
              variant="ghost"
              className="w-full shrink-0 rounded-none py-3 bg-transparent text-muted-foreground hover:bg-transparent hover:text-foreground"
              onClick={() => navigate('/admin/dashboard-builder')}
            >
              <LayoutTemplate className="h-4 w-4 mr-2" />
              Edit Widgets
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};
